import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from '../services/api.service';
import { UtilitiesService } from '../services/utilities.service';
import Swal from 'sweetalert2';

interface LeaveForm {
  from_date: string | null;
  to_date: string | null;
  leave_type: string;
  notes: string | null;
}

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
    standalone: false
})
export class ProfileComponent implements OnInit {

  id: any;
  token: any;
  user: any;
  employee: any;

  Swal = 'sweetalert2';

  user_password = {
    id: null,
    email: '',
    password: '',
  };

  slots: any[] = [];

  slot = {
    date: '',
    doctor_id: null,
    reason: '',
    period: 30
  };

  myLeaves: any[] = [];
  leaveBalance: any = null;
  leavesMonths: number | null = null;
  modalMode: 'create' | 'edit' = 'create';
  editingLeaveId: string | null = null;
  submitting = false;

  leaveForm: LeaveForm = this.emptyLeaveForm();

  readonly leaveTypes = [
    { value: 'ANNUAL', label: 'Annual', badge: 'bg-primary' },
    { value: 'SICK', label: 'Sick', badge: 'bg-warning text-dark' },
    { value: 'PERSONAL', label: 'Without Payment', badge: 'bg-secondary' },
    { value: 'LEAVE', label: 'Leave', badge: 'bg-info text-dark' },
    { value: 'LONG', label: 'Long', badge: 'bg-dark' },
    { value: 'OTHER', label: 'Other', badge: 'bg-light text-dark border' }
  ];

  constructor(
    public utility: UtilitiesService,
    private router: Router,
    public api: ApiService,
    private modalService: NgbModal
  ) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
    this.id = localStorage.getItem('id');
  }

  ngOnInit(): void {
    this.getUser();
  }

  get isLeaveFormValid(): boolean {
    return !!(
      this.employee &&
      this.leaveForm.from_date &&
      this.leaveForm.to_date &&
      this.leaveForm.leave_type &&
      this.leaveForm.leave_type !== '0' &&
      this.previewDays !== null &&
      this.previewDays > 0
    );
  }

  get previewDays(): number | null {
    if (!this.leaveForm.from_date || !this.leaveForm.to_date) {
      return null;
    }
    return this.calcDays(this.leaveForm.from_date, this.leaveForm.to_date);
  }

  changeP(content: any, user: any) {
    this.user_password.id = user.id;
    this.user_password.email = user.email;
    this.user_password.password = user.password;

    this.modalService.open(content);
  }

  open(content: any) {
    this.modalService.open(content);
  }

  openCreateLeave(modal: unknown): void {
    if (!this.employee) {
      Swal.fire({
        title: 'Not available',
        text: 'Leave requests are only available for registered employees.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.modalMode = 'create';
    this.editingLeaveId = null;
    this.leaveForm = this.emptyLeaveForm();
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  openEditLeave(modal: unknown, leave: any): void {
    this.modalMode = 'edit';
    this.editingLeaveId = leave.id;
    this.leaveForm = {
      from_date: this.toInputDate(leave.from_date),
      to_date: this.toInputDate(leave.to_date),
      leave_type: leave.leave_type ?? 'ANNUAL',
      notes: leave.notes
    };
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  getUser() {
    this.utility.loading = true;
    let id = localStorage.getItem('id');
    if (id) {
      const sub = this.api.get('users/' + id, this.token).subscribe(
        async data => {
          let objects: any = {
            users: []
          };
          objects = data;

          this.user = objects.users[0][0];
        },
        async error => { }
      );

      sub.add(() => { this.getDetails(id); });
    }
  }

  getDetails(id: any) {
    const sub = this.api.get('employees/user/' + id, this.token).subscribe(
      async data => {
        let objects: any = {};
        objects = data;

        this.employee = objects;
      },
      async error => { }
    );
    sub.add(() => {
      if (this.employee?.id) {
        this.loadEmployeeLeaveData();
      } else {
        this.utility.loading = false;
      }
    });
  }

  loadEmployeeLeaveData(): void {
    this.loadMyLeaves();
    this.loadLeaveBalance();
  }

  loadLeaveBalance(): void {
    if (!this.employee?.id) {
      return;
    }

    this.api.get('leaves/stats/' + this.employee.id, this.token).subscribe({
      next: (data: any) => {
        this.leaveBalance = data ?? null;
        if (this.leaveBalance?.updated_at) {
          this.leavesMonths = this.monthsBetween(this.leaveBalance.updated_at);
        }
      },
      error: () => {
        this.leaveBalance = null;
        this.leavesMonths = null;
      }
    });
  }

  loadMyLeaves(): void {
    if (!this.employee?.id) {
      return;
    }

    this.utility.loading = true;
    this.api.get('leaves/employee/' + this.employee.id, this.token).subscribe({
      next: (data: any) => {
        const leaves: any[] = data ?? [];
        this.myLeaves = leaves
          .map(item => ({
            ...item,
            dayDiff: this.calcDays(item.from_date, item.to_date)
          }))
          .sort((a, b) => new Date(b.from_date).getTime() - new Date(a.from_date).getTime());
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'Could not load your leave requests.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.utility.loading = false;
      }
    });
  }

  getLeaveTypeMeta(type: string) {
    return this.leaveTypes.find(t => t.value === type) ?? {
      value: type,
      label: type,
      badge: 'bg-secondary'
    };
  }

  saveLeave(): void {
    if (!this.isLeaveFormValid || this.submitting || !this.employee) {
      return;
    }

    const body = {
      employee_id: this.employee.id,
      employee_name: this.employee.name ?? '',
      from_date: this.leaveForm.from_date,
      to_date: this.leaveForm.to_date,
      leave_type: this.leaveForm.leave_type,
      notes: this.leaveForm.notes
    };

    this.submitting = true;
    const request =
      this.modalMode === 'edit' && this.editingLeaveId
        ? this.api.update('leaves/' + this.editingLeaveId, body, this.token)
        : this.api.post('leaves/', body, this.token);

    request.subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.loadEmployeeLeaveData();
        Swal.fire({
          title: 'Success',
          text:
            this.modalMode === 'edit'
              ? 'Leave request updated.'
              : 'Leave request submitted.',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000,
          showConfirmButton: true
        });
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'Something went wrong. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  getMySlots(id: any) {
    const sub = this.api.get('schedules/' + id, this.token).subscribe(
      async data => {
        let objects: any = {};
        objects = data;

        this.slots = objects.schedules;
      },
      async error => { }
    );
    sub.add(() => { this.utility.loading = false; });
  }

  OnChangePassword(id: any) {
    const body = {
      password: this.user_password.password
    };

    this.api.update("users/password/" + id, body, this.token).subscribe(
      async data => {
        Swal.fire({
          title: 'Success!',
          text: 'Password Changed Successfuly!',
          icon: 'success',
          confirmButtonText: 'Cool'
        });
      },
      async error => {
        Swal.fire({
          title: 'Error!',
          text: 'Something went wrong!',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    );

    this.modalService.dismissAll();
  }

  createSlot() {
    if (this.employee) {
      const body = {
        date_time: this.slot.date,
        period: this.slot.period,
        active: true,
        blocked: true,
        doctor_id: this.employee.id,
        reason: this.slot.reason,
      };

      const sub = this.api.post("schedules/", body, this.token).subscribe(
        async data => {
          let objects: any = {};
          objects = data;

          Swal.fire({
            title: 'Success!',
            text: 'Slot Booked Successfuly!',
            icon: 'success',
            confirmButtonText: 'Cool'
          });
        },
        async error => {
          Swal.fire({
            title: 'Error!',
            text: 'Something went wrong!',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      );
    }
    else {
      Swal.fire({
        title: 'info!',
        text: 'You are not a Clinician!',
        icon: 'warning',
        confirmButtonText: 'Ok'
      });
    }
  }

  saveDoctor(doctor: any) {
    localStorage.setItem('doctor', JSON.stringify(doctor));
  }

  monthsBetween(value: string): number {
    const today = new Date();
    const date = new Date(value);
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    return (todayYear - dateYear) * 12 + (todayMonth - dateMonth);
  }

  private emptyLeaveForm(): LeaveForm {
    return {
      from_date: null,
      to_date: null,
      leave_type: 'ANNUAL',
      notes: null
    };
  }

  private calcDays(from: string, to: string): number {
    const date1 = new Date(from);
    const date2 = new Date(to);
    const timeDiff = date2.getTime() - date1.getTime();
    return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
  }

  private toInputDate(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return value;
    }
    return d.toISOString().slice(0, 10);
  }
}
